import { useCallback, useEffect, useState } from 'react';
import { api, operateur } from '../api.js';

const LIBELLES_ROLE = {
  maitreOeuvre: 'Maître d\u2019œuvre',
  maitreOuvrage: 'Maître d\u2019ouvrage',
  proprietaire: 'Propriétaire',
  representant: 'Représentant',
  etna: 'Représentant Etna',
};

const LIBELLES_PHASE = { reception: 'Réception', levee: 'Levée des réserves' };

/**
 * File de suivi des signatures envoyées à distance et pas encore bouclées, filtrée par
 * le matricule courant (comme la levée). Permet de voir qui a signé, qui reste, et de
 * relancer par e-mail le signataire dont c'est le tour.
 */
export function SignaturesEnAttente({ onQuitter }) {
  const [etat, setEtat] = useState('chargement'); // chargement | liste | erreur
  const [demandes, setDemandes] = useState([]);
  const [message, setMessage] = useState(null);
  const [relanceEnCours, setRelanceEnCours] = useState(null); // signerId en cours
  const [confirmAnnul, setConfirmAnnul] = useState(null); // demandeId à confirmer
  const [annulEnCours, setAnnulEnCours] = useState(null); // demandeId en cours d'annulation

  const matricule = operateur.courant();

  const charger = useCallback(async () => {
    setEtat('chargement');
    setMessage(null);
    try {
      const { demandes: d } = await api.signaturesEnAttente(matricule);
      setDemandes(d ?? []);
      setEtat('liste');
    } catch (err) {
      setMessage(err.message || 'Chargement impossible.');
      setEtat('erreur');
    }
  }, [matricule]);

  useEffect(() => { charger(); }, [charger]);

  const relancer = async (demandeId, signerId) => {
    setRelanceEnCours(signerId);
    setMessage(null);
    try {
      await api.relancerSignataire(demandeId, signerId);
      setMessage('Relance envoyée.');
    } catch (err) {
      setMessage(err.erreur || err.message || 'Relance impossible.');
    } finally {
      setRelanceEnCours(null);
    }
  };

  const annuler = async (demandeId) => {
    setAnnulEnCours(demandeId);
    setMessage(null);
    try {
      await api.annulerDemande(demandeId);
      setConfirmAnnul(null);
      setMessage('Demande annulée.');
      await charger(); // la ligne quitte la liste
    } catch (err) {
      setMessage(err.erreur || err.message || 'Annulation impossible.');
    } finally {
      setAnnulEnCours(null);
    }
  };

  if (etat === 'chargement') {
    return <div className="etat"><div className="titre">Chargement…</div></div>;
  }

  if (etat === 'erreur') {
    return (
      <>
        <div className="etat"><div className="titre">Suivi indisponible</div>
          <p className="sous-titre">{message}</p>
        </div>
        <nav className="barre">
          <button type="button" onClick={charger}>Réessayer</button>
          <button type="button" onClick={onQuitter}>Retour</button>
        </nav>
      </>
    );
  }

  return (
    <>
      <h2>Signatures en attente</h2>
      <p className="sous-titre">
        {demandes.length === 0
          ? 'Aucune signature à distance en cours à votre matricule.'
          : `${demandes.length} document${demandes.length > 1 ? 's' : ''} envoyé${demandes.length > 1 ? 's' : ''} à signer, pas encore bouclé${demandes.length > 1 ? 's' : ''}.`}
      </p>

      {message && <div className="carte info" role="status">{message}</div>}

      {demandes.map((d) => {
        // Le prochain à signer = le premier « en attente » (ordre séquentiel).
        const prochain = d.signataires.find((s) => s.statut !== 'signe');
        return (
          <div className="carte affaire" key={d.demandeId}>
            <div className="affaire-titre">{d.nomAffaire}</div>
            <div className="affaire-meta">
              {LIBELLES_PHASE[d.phase] ?? d.phase} · appareil {d.appareilNo} · réf. {d.reference}
              {d.mode === 'distance' ? ' · à distance' : ' · sur place'}
            </div>
            <ul className="liste-signataires">
              {d.signataires.map((s) => (
                <li key={s.signerId} className={s.statut === 'signe' ? 'signe' : 'attente'}>
                  <span className="role">{LIBELLES_ROLE[s.role] ?? s.role} — {s.nomComplet}</span>
                  <span className="statut">{s.statut === 'signe' ? '✓ signé' : 'en attente'}</span>
                </li>
              ))}
            </ul>
            {d.mode === 'distance' && prochain && (
              <button
                type="button"
                className="bouton-affaire"
                disabled={relanceEnCours === prochain.signerId}
                onClick={() => relancer(d.demandeId, prochain.signerId)}
              >
                {relanceEnCours === prochain.signerId
                  ? 'Relance…'
                  : `Relancer ${LIBELLES_ROLE[prochain.role] ?? prochain.role}`}
              </button>
            )}

            {confirmAnnul === d.demandeId ? (
              <div className="confirm-annul">
                <span>Annuler cette demande ? Le client ne pourra plus signer ce document.</span>
                <div className="confirm-actions">
                  <button
                    type="button"
                    className="bouton-danger"
                    disabled={annulEnCours === d.demandeId}
                    onClick={() => annuler(d.demandeId)}
                  >
                    {annulEnCours === d.demandeId ? 'Annulation…' : 'Oui, annuler'}
                  </button>
                  <button type="button" onClick={() => setConfirmAnnul(null)}>Non</button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="lien-annuler"
                onClick={() => { setMessage(null); setConfirmAnnul(d.demandeId); }}
              >
                Annuler la demande
              </button>
            )}
          </div>
        );
      })}

      <nav className="barre">
        <button type="button" onClick={charger}>Rafraîchir</button>
        <button type="button" onClick={onQuitter}>Retour</button>
      </nav>
    </>
  );
}
